import { useState, useEffect } from 'react';
import { favoritesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    Heart, ArrowLeft, Loader2, Trash2, ExternalLink, 
    Briefcase, Search
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function FavoritesPage() {
    const [favorites, setFavorites] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        loadFavorites();
    }, []);

    const loadFavorites = async () => {
        try {
            const data = await favoritesApi.list();
            setFavorites(data);
        } catch (error) {
            console.error('Error loading favorites:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemove = async (favoriteId) => {
        try {
            await favoritesApi.remove(favoriteId);
            setFavorites(prev => prev.filter(f => f.favorite_id !== favoriteId));
            toast.success('Removed from favorites');
        } catch (error) {
            toast.error('Failed to remove');
        }
    };

    const handleMarkApplied = async (favorite) => {
        try {
            const { appliedApi } = await import('../lib/api');
            await appliedApi.add(favorite.job);
            toast.success('Marked as applied');
        } catch (error) {
            toast.error(error.message || 'Failed to mark as applied');
        }
    };

    const getMatchColor = (score) => {
        if (score >= 70) return 'text-acid';
        if (score >= 40) return 'text-neon-cyan';
        return 'text-muted-foreground';
    };

    return (
        <div className="min-h-screen bg-void page-enter">
            <div className="noise-overlay" />

            {/* Header */}
            <header className="sticky top-0 z-20 glass border-b border-border">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
                    <Link 
                        to="/dashboard" 
                        className="text-muted-foreground hover:text-acid transition-colors"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
                    </Link>
                    <h1 className="font-display text-xl font-bold text-white flex items-center gap-2">
                        <Heart className="w-5 h-5 text-neon-cyan" strokeWidth={1.5} />
                        Saved Jobs
                    </h1>
                    {favorites.length > 0 && (
                        <Badge className="ml-auto font-mono text-xs bg-neon-cyan/10 text-neon-cyan border-neon-cyan/30">
                            {favorites.length} saved
                        </Badge>
                    )}
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-6 py-8">
                {isLoading ? (
                    <div className="text-center py-20">
                        <Loader2 className="w-8 h-8 text-acid animate-spin mx-auto mb-4" />
                        <p className="text-muted-foreground text-sm">Loading favorites...</p>
                    </div>
                ) : favorites.length === 0 ? (
                    <Card className="bg-void-paper border-border">
                        <CardContent className="text-center py-20">
                            <Heart className="w-16 h-16 text-muted-foreground/30 mx-auto mb-6" strokeWidth={1.5} />
                            <h2 className="font-display text-xl font-bold text-white mb-2">
                                No saved jobs yet
                            </h2>
                            <p className="text-muted-foreground text-sm mb-6">
                                Save jobs you're interested in to review them later
                            </p>
                            <Link to="/dashboard">
                                <Button className="bg-acid text-black font-mono uppercase tracking-wider text-sm hover:bg-acid-hover">
                                    <Search className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                    Find Jobs
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {favorites.map((favorite) => (
                            <Card 
                                key={favorite.favorite_id}
                                className="bg-void-paper border-border card-hover group"
                                data-testid={`favorite-${favorite.favorite_id}`}
                            >
                                <CardContent className="p-5">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="font-display text-lg font-bold text-white truncate group-hover:text-acid transition-colors">
                                                    {favorite.job.title}
                                                </h3>
                                                {favorite.job.match_score !== undefined && favorite.job.match_score !== null && (
                                                    <Badge className={`font-mono text-xs ${getMatchColor(favorite.job.match_score)} border-current bg-current/10`}>
                                                        {favorite.job.match_score}% match
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-muted-foreground text-sm font-medium mb-2">
                                                {favorite.job.company}
                                            </p>
                                            <p className="text-muted-foreground/70 text-sm line-clamp-2 mb-3">
                                                {favorite.job.description}
                                            </p>
                                            <div className="flex items-center gap-4 text-xs text-muted-foreground font-mono">
                                                {favorite.job.location && (
                                                    <span>{favorite.job.location}</span>
                                                )}
                                                <span className="text-muted-foreground/50">
                                                    Saved {new Date(favorite.saved_at).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleMarkApplied(favorite)}
                                                className="text-muted-foreground hover:text-cyber-purple"
                                                data-testid={`apply-${favorite.favorite_id}`}
                                            >
                                                <Briefcase className="w-4 h-4" strokeWidth={1.5} />
                                            </Button>
                                            <a
                                                href={favorite.job.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-neon-cyan transition-colors"
                                            >
                                                <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                                            </a>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleRemove(favorite.favorite_id)}
                                                className="text-muted-foreground hover:text-neon-red"
                                                data-testid={`remove-${favorite.favorite_id}`}
                                            >
                                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
